import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, showErrorNotification, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { BaseCompAction, BaseCompSubAction, BaseInfoCompProps, capitalizeAll, getErrorMessage } from '@moderepo/mode-app-components';
import { Agent, AgentRole, ApiError } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { DashboardWidgetPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { EntitiesMapDashboardWidgetSettings, moveWidgetToGroup, WidgetGroupWidgetSettings } from '.';
import { EntitiesMapContainer, EntitiesMapContainerProps, getMultipleEntityClassesEntities } from '..';
import { WidgetDataViewPlaceholderComp } from '../../components';
import { isAlertingFeatureEnabled } from '../../utils/utils';



const useStyle = makeStyles(() => {
    return {
        widget: {
            width : '100%',
            height: '100%',
            zIndex: 0,
        },

        placeholderWidget: {
            width : '100%',
            height: '100%',
        },
    };
}, { name: 'EntitiesMapDashboardWidgetViewContainer', index: 2 });



export interface EntitiesMapDashboardWidgetViewContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly dashboardId: number;
    readonly selectedWidget: EntitiesMapDashboardWidgetSettings;

    // An array of widgets of type WidgetGroup. This will be used to display the list of widget group this widget can be moved into
    readonly widgetGroups: readonly WidgetGroupWidgetSettings[];
    readonly isInMoveAndResizeMode?: boolean | undefined;

    // The handler for when the widget itself wants to enable move and resize mode. The widget itself doesn't control the mode so it will
    // call the handler to handle the event
    readonly enableMoveAndResizeModeHandler?: ()=> void;

    // This is a container that wraps a DUMB component and some time we need to pass some props such as className, styles, etc... into the
    // component from the container of this container so this is a way for us to pass props into the component
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
    } | undefined;


    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }
}



/**
 * This is the component for displaying the UI/data view for the EntitiesMapDashboardWidget.
 */
export const EntitiesMapDashboardWidgetViewContainer: React.FC<EntitiesMapDashboardWidgetViewContainerProps> = (
    props: EntitiesMapDashboardWidgetViewContainerProps,
) => {

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const canEditDashboard = projectAgent?.role === AgentRole.ADMIN;
    const alertingFeatureEnabled = isAlertingFeatureEnabled(project, loggedInAgent);

    /**
     * This state is used for keeping track of the list of entities that belonged to the entityClasses specified in the widget settings
     * AND the entities specified in the widget's entities array.
     */
    const [selectedEntityIds, setSelectedEntityIds] = useState<readonly string[]>();

    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    /**
     * On Show Dashboard Widget settings button clicked, we will show the dashboard widget's settings in a Slide-out panel
     */
    const showDashboardWidgetPreview = useCallback(() => {
        const options: DashboardWidgetPreviewCompOptions = {
            compType : SlideOutPanelCompType.DASHBOARD_WIDGET_PREVIEW,
            location : SlideOutPanelLocation.RIGHT,
            compProps: {
                data: {
                    projectId     : props.projectId,
                    dashboardId   : props.dashboardId,
                    selectedWidget: props.selectedWidget.id,
                },
                routeContext: props.routeContext,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.routeContext, props.dashboardId, props.projectId, props.selectedWidget.id, uiDispatch]);



    /**
     * Create the Actions to show in the widget's panel header, the top right corner.
     */
    const actions = useMemo((): EntitiesMapContainerProps['customSecondaryActions'] => {
        const groupingActions: (BaseCompAction | BaseCompSubAction)[] = [];

        // If there are 1 or more groups, add options to move this widget to these groups
        if (props.widgetGroups && props.widgetGroups.length > 0) {
            groupingActions.push({
                icon   : 'fas fa-object-group',
                label  : capitalizeAll(trans('dashboard.action.move_to_group')),
                actions: props.widgetGroups.map((widgetGroup) => {
                    return {
                        label   : widgetGroup.title,
                        icon    : widgetGroup.icon,
                        disabled: widgetGroup.id === props.selectedWidget.widgetGroupId,
                        onClick : () => {
                            moveWidgetToGroup({
                                targetWidget: props.selectedWidget,
                                widgetGroup,
                                uiDispatch,
                                dataState,
                                dataDispatch,
                                appDataState,
                                appDataDispatch,
                                loggedInAgent,
                                projectId   : props.projectId,
                                homeId      : props.routeContext?.homeId,
                                dashboardId : props.dashboardId,
                                trans,
                            });
                        },
                    };
                }),
            });
        }

        // If this widget is already in a group, add an option to remove it from the group
        if (props.selectedWidget.widgetGroupId) {
            groupingActions.push({
                icon   : 'fas fa-object-ungroup',
                label  : capitalizeAll(trans('dashboard.action.remove_from_group')),
                onClick: () => {
                    moveWidgetToGroup({
                        targetWidget: props.selectedWidget,
                        widgetGroup : undefined,
                        uiDispatch,
                        dataState,
                        dataDispatch,
                        appDataState,
                        appDataDispatch,
                        loggedInAgent,
                        projectId   : props.projectId,
                        homeId      : props.routeContext?.homeId,
                        dashboardId : props.dashboardId,
                        trans,
                    });
                },
            });
        }


        if (canEditDashboard && !props.isInMoveAndResizeMode) {
            return [{
                icon   : 'settings',
                label  : capitalizeAll(trans('label.settings')),
                onClick: showDashboardWidgetPreview,
            },
            ...groupingActions,
            {
                icon   : 'open_with',
                label  : capitalizeAll(trans('dashboard.action.move_and_resize_widgets')),
                onClick: () => {
                    if (props.enableMoveAndResizeModeHandler) {
                        props.enableMoveAndResizeModeHandler();
                    }
                },
            }];
        }
        return undefined;
    }, [appDataDispatch, appDataState, canEditDashboard, dataDispatch, dataState, loggedInAgent, props, showDashboardWidgetPreview, trans,
        uiDispatch]);



    /**
     * This useEffect is used for collecting all entityIds oto be passed to the EntitiesMapContainer. If the widget's "entityClasses" has
     * 1 or more entityClasses, we need to fetch all the entities for those entity classes and get their IDs
     *
     */
    useEffect(() => {

        // Fetch all the entities for the selected entityClasses. Although we only need their IDs, we have to fetch all the entities first.
        getMultipleEntityClassesEntities(
            props.projectId,
            props.selectedWidget.homeId,
            props.selectedWidget.entityClasses ?? [],
            appDataDispatch,
        ).then((entities) => {
            // Get the entityId from all the entities
            const entityIds = entities.map((entity) => {
                return entity.entityId;
            });

            // Add the widget.entityIds to the result. We don't need to fetch any data since all we need are the ids.
            if (props.selectedWidget.entityIds) {
                // If it possible that the user select an entity that is already included in the list of entities from the entityClasses
                // so we need to make sure we don't include those entities multiple times.
                props.selectedWidget.entityIds.forEach((entityId) => {
                    if (!entityIds.includes(entityId)) {
                        entityIds.push(entityId);
                    }
                });
            }


            // We have all the entityIds, set it to the state.
            setSelectedEntityIds(entityIds);
        }).catch((error) => {
            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
            setSelectedEntityIds([]);
        });
    }, [appDataDispatch, props.projectId, props.selectedWidget.entityClasses, props.selectedWidget.entityIds, props.selectedWidget.homeId,
        uiDispatch]);


    return (
        <>
            {selectedEntityIds ? (
                <EntitiesMapContainer
                    className={clsx(props.className, classes.widget)}
                    key={JSON.stringify(selectedEntityIds)}
                    title={props.selectedWidget.title}
                    icon={props.selectedWidget.icon}
                    customSecondaryActions={actions}
                    infoCompProps={props.infoCompProps}
                    projectId={props.projectId}
                    entityIds={selectedEntityIds}
                    showAlertingState={alertingFeatureEnabled}
                    excludeEntitiesWithoutLocation={props.selectedWidget.viewSettings.excludeEntitiesWithoutLocation}
                />
            ) : (
                <WidgetDataViewPlaceholderComp
                    className={clsx(classes.placeholderWidget, props.infoCompProps?.className)}
                    title={props.selectedWidget.title}
                    icon={props.selectedWidget.icon}
                    customSecondaryActions={actions}
                    showCustomActionOnHover
                />
            )}
        </>
    );
};
